import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { AiManagedObject } from '@atlassian/ai-managed-objects/types';

import type {
	AiManagedObjectLoadStatus,
	AgentManagedExtensionAttributes,
	AgentManagedExtensionTemplateConfig,
	AgentManagedExtensionPlugin,
} from '../../common/agentManagedExtensionPluginType';
import { getNodeAiManagedObject, getNodeTemplateConfig } from '../../common/utils';
import {
	type AgentManagedExtensionNodeWithPos,
	findConfiguredAgentManagedExtension,
} from '../editor-commands/agent-managed-object-node';
import {
	getAiManagedObjectLoadStatusFromPluginState,
	getCachedAiManagedObjectFromPluginState,
} from '../pm-plugins/state';

type UseConfiguredAgentManagedExtensionArgs = {
	api: ExtractInjectionAPI<AgentManagedExtensionPlugin> | undefined;
	editorView: EditorView;
};

type UseConfiguredAgentManagedExtensionResult = {
	aimoId?: string;
	aimoLoadStatus?: AiManagedObjectLoadStatus;
	blockElement: HTMLElement | null;
	cachedAimo?: AiManagedObject;
	configLocalId?: string;
	found: AgentManagedExtensionNodeWithPos | null | undefined;
	foundPos?: number;
	isOpen: boolean;
	templateConfig?: AgentManagedExtensionTemplateConfig;
};

export const useConfiguredAgentManagedExtension = ({
	api,
	editorView,
}: UseConfiguredAgentManagedExtensionArgs): UseConfiguredAgentManagedExtensionResult => {
	const agentManagedExtensionState = useSharedPluginStateWithSelector(
		api,
		['agentManagedExtension'],
		({ agentManagedExtensionState }) => agentManagedExtensionState,
	);

	const isOpen = agentManagedExtensionState?.isConfigOpen ?? false;
	const configLocalId = agentManagedExtensionState?.configLocalId;
	const found = isOpen ? findConfiguredAgentManagedExtension(editorView, configLocalId) : null;
	const dom = found ? editorView.nodeDOM(found.pos) : null;
	const blockElement = dom instanceof HTMLElement ? dom : null;
	const foundPos = found?.pos;
	const foundAttrs = found?.node.attrs as AgentManagedExtensionAttributes | undefined;
	const aimoId = getNodeAiManagedObject(foundAttrs)?.id;
	const templateConfig = getNodeTemplateConfig(foundAttrs);
	const cachedAimo = aimoId
		? getCachedAiManagedObjectFromPluginState(agentManagedExtensionState, aimoId)
		: undefined;
	const aimoLoadStatus = getAiManagedObjectLoadStatusFromPluginState(
		agentManagedExtensionState,
		aimoId,
	);

	return {
		aimoId: aimoId,
		aimoLoadStatus: aimoLoadStatus,
		blockElement: blockElement,
		cachedAimo: cachedAimo,
		configLocalId: configLocalId,
		found: found,
		foundPos: foundPos,
		isOpen: isOpen,
		templateConfig: templateConfig,
	};
};
